import type { Module } from "vuex";
import type { Client, EditableTypeType } from "shared-types";
import { EditableType } from "shared-utils/enums/editables";

import type { Asset } from "@/types/types";

import { deepClone } from "@/utils";

import type { RootState } from "@/store/types";

export const defaultMediaLibrarySortBy = "name_ASC";

const storeNamespace = "mediaLibrary";

export const MediaLibraryMutation = {
    SetActiveFile: "setActiveFile",
    SetActiveFolderId: "setActiveFolderId",
    SetActiveInstance: "setActiveInstance",
    SetCloseCallback: "setCloseCallback",
    SetOpen: "setOpen",
    SetSandboxSelectedClient: "setSandboxSelectedClient",
    SetShowListView: "setShowListView",
    SetShowGridView: "setShowGridView",
    SetSortBy: "setSortBy",
    SetUseAutoResize: "setUseAutoResize",
    ResetState: "resetState"
};

export const MediaLibraryAction = {
    Open: `${storeNamespace}/open`,
    Close: `${storeNamespace}/close`,
    SelectFile: `${storeNamespace}/selectFile`,
    SelectFiles: `${storeNamespace}/selectFiles`,
    SelectFolder: `${storeNamespace}/selectFolder`,
    SetActiveFile: `${storeNamespace}/${MediaLibraryMutation.SetActiveFile}`,
    SetActiveFolderId: `${storeNamespace}/${MediaLibraryMutation.SetActiveFolderId}`,
    SetSandboxSelectedClient: `${storeNamespace}/${MediaLibraryMutation.SetSandboxSelectedClient}`,
    SetSortBy: `${storeNamespace}/${MediaLibraryMutation.SetSortBy}`,
    SetUseAutoResize: `${storeNamespace}/${MediaLibraryMutation.SetUseAutoResize}`,
    ResetState: `${storeNamespace}/${MediaLibraryMutation.ResetState}`,
    SetShowGridView: `${storeNamespace}/${MediaLibraryMutation.SetShowGridView}`,
    SetShowListView: `${storeNamespace}/${MediaLibraryMutation.SetShowListView}`
};

export const MediaLibraryTab = {
    Library: "library",
    Link: "link"
};

type OpenFile = {
    instance: { multiple: boolean; type: EditableTypeType } | undefined;
    folderId?: string;
    fileId?: string;
    closeCallback?: (data: Asset[] | null) => void;
};

export type State = {
    activeFile: string | null;
    activeFolderId: string | null;
    activeInstance: OpenFile["instance"] | null;
    closeCallback: ((assets: Asset[] | null) => void) | null;
    open: OpenFile | false;
    sandboxSelectedClient: Client | null;
    showGridView: boolean;
    sortBy: string;
    useAutoResize: boolean;
};

const getDefaultState = (): State => ({
    activeFile: null,
    activeFolderId: null,
    activeInstance: null,
    closeCallback: null,
    open: false,
    sandboxSelectedClient: null,
    showGridView: true,
    sortBy: defaultMediaLibrarySortBy,
    useAutoResize: false
});

const mediaLibraryModule: Module<State, RootState> = {
    namespaced: true,
    state: getDefaultState(),
    getters: {
        activeFolderName: (state: State) =>
            state.activeFolderId
                ?.split("/")
                .filter(folder => folder.trim() !== "")
                .pop() ?? ""
    },
    mutations: {
        setActiveFile(state, fileId: State["activeFile"]) {
            state.activeFile = fileId;
        },

        setActiveFolderId(state, activeFolderId: State["activeFolderId"]) {
            state.activeFolderId = activeFolderId;
        },

        setActiveInstance(state, payload: { instance: State["activeInstance"] }) {
            state.activeInstance = payload.instance;
        },

        setCloseCallback(state, payload: { callback: State["closeCallback"] }) {
            state.closeCallback = payload.callback;
        },

        setOpen(state, payload: { open: State["open"] }) {
            state.open = payload.open;
        },

        setShowListView(state) {
            state.showGridView = false;
        },

        setShowGridView(state) {
            state.showGridView = true;
        },

        setSortBy(state, sortBy: State["sortBy"]) {
            state.sortBy = sortBy;
        },

        setSandboxSelectedClient(state, client: State["sandboxSelectedClient"]) {
            state.sandboxSelectedClient = client;
        },

        setUseAutoResize(state, useAutoResize: State["useAutoResize"]) {
            state.useAutoResize = useAutoResize;
        },

        resetState(state) {
            Object.assign(state, getDefaultState());
        }
    },
    actions: {
        close({ commit, state }, selectedAssets: Asset[] | null = null) {
            if (typeof state.closeCallback === "function") {
                state.closeCallback(selectedAssets);
            }

            commit(MediaLibraryMutation.SetActiveInstance, { instance: null });
            commit(MediaLibraryMutation.SetCloseCallback, { callback: null });
            commit(MediaLibraryMutation.SetOpen, { open: false });
        },

        open({ commit }, payload: OpenFile) {
            commit(MediaLibraryMutation.SetActiveInstance, { instance: payload.instance ?? null });
            commit(MediaLibraryMutation.SetCloseCallback, { callback: payload.closeCallback });
            commit(MediaLibraryMutation.SetOpen, { open: true });
        },

        selectFile({ commit, dispatch }, payload: OpenFile) {
            return new Promise(resolve => {
                const openPayload = deepClone(payload);

                openPayload.instance = openPayload.instance ?? {
                    type: EditableType.Image,
                    multiple: false
                };

                if ("folderId" in payload) {
                    commit(MediaLibraryMutation.SetActiveFolderId, payload.folderId);
                }

                commit(MediaLibraryMutation.SetActiveFile, payload.fileId);

                if (!payload.closeCallback) {
                    openPayload.closeCallback = data => {
                        resolve(data);
                    };
                }

                void dispatch("open", openPayload);
            });
        },

        selectFiles({ commit, dispatch }, payload: OpenFile) {
            return new Promise(resolve => {
                const openPayload = deepClone(payload);

                openPayload.instance = openPayload.instance ?? {
                    type: EditableType.Image,
                    multiple: true
                };

                if ("folderId" in payload) {
                    commit(MediaLibraryMutation.SetActiveFolderId, payload.folderId);
                }

                if (!payload.closeCallback) {
                    openPayload.closeCallback = data => {
                        resolve(data);
                    };
                }

                void dispatch("open", openPayload);
            });
        },

        selectFolder({ commit, dispatch }, payload: OpenFile) {
            return new Promise(resolve => {
                const openPayload = deepClone(payload);

                openPayload.instance = openPayload.instance ?? {
                    type: "folder",
                    multiple: false
                };

                if ("folderId" in payload) {
                    commit(MediaLibraryMutation.SetActiveFolderId, payload.folderId);
                }

                if (!payload.closeCallback) {
                    openPayload.closeCallback = data => {
                        resolve(data);
                    };
                }

                void dispatch("open", openPayload);
            });
        },

        setActiveFile({ commit }, fileId: State["activeFile"]) {
            commit(MediaLibraryMutation.SetActiveFile, fileId);
        },

        setActiveFolderId({ commit }, folderId: State["activeFolderId"]) {
            commit(MediaLibraryMutation.SetActiveFolderId, folderId);
        },

        setSortBy({ commit }, sortBy: State["sortBy"]) {
            commit(MediaLibraryMutation.SetSortBy, sortBy);
        },

        setSandboxSelectedClient({ commit }, client: State["sandboxSelectedClient"]) {
            commit(MediaLibraryMutation.SetSandboxSelectedClient, client);
        },

        setUseAutoResize({ commit }, useAutoResize: State["useAutoResize"]) {
            commit(MediaLibraryMutation.SetUseAutoResize, useAutoResize);
        },

        resetState({ commit }) {
            commit(MediaLibraryMutation.ResetState);
        }
    }
};

export default mediaLibraryModule;
